import { Injectable, signal, computed, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { CommonService } from '../common-service';
import { UnifiedAuthService } from '../unified-auth.service';
import {
  AiMessage, AiState, AiOpcao, AiResultados, Acao, Fonte,
  ConverseResponse, ConfirmResponse, VoiceResponse,
  AlertaAssistente, AlertasResponse, AlertaAcaoResponse
} from '../../component/ai-assistant/ai-agent.models';
import { audioVazio } from '../../utils/audio-silencio.util';
import { lerEventosSse } from '../../utils/sse-stream.util';
import { ScreenContextService } from '../screen-context.service';

/**
 * Cliente do AI Agent (Orchestrator.Data). Mantém o estado da conversa em signals.
 * Nunca toca na chave do Gemini (fica só no backend). Auth via JWT do sessionStorage.
 */
@Injectable({ providedIn: 'root' })
export class AiAgentService extends CommonService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(UnifiedAuthService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly screenContext = inject(ScreenContextService);

  private readonly base = environment.urlApiOrchestratorData + 'AIAgent/';

  readonly aberto = signal(false);
  readonly estado = signal<AiState>('ocioso');
  readonly mutado = signal<boolean>(false);
  readonly mensagens = signal<AiMessage[]>([]);
  readonly sessionId = signal<string | null>(null);
  readonly prefill$ = new Subject<string>();
  /**
   * Último prefill ainda NÃO consumido pela conversa. Existe porque `prefill$` é um Subject sem
   * replay: a `search-bar` faz `abrir(); prefill$.next(valor)` no mesmo tick, e o `ai-conversa`
   * (montado dentro do `@if (aberto())` da casca desde a T3) só assinaria na change detection
   * seguinte — a emissão se perdia. O componente lê este signal, aplica e devolve `null`.
   * `prefill$` continua público para quem já emite.
   */
  readonly prefillPendente = signal<string | null>(null);

  /**
   * Trava anti-pergunta-dupla (comportamento WhatsApp adaptado): true entre o envio e a chegada
   * da resposta. Novas perguntas são ignoradas enquanto true. Fallback: se a resposta não vier
   * em {@link FALLBACK_BLOQUEIO_MS}, a trava abre sozinha para o usuário não ficar preso.
   */
  readonly ocupado = signal(false);
  private bloqueioTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly FALLBACK_BLOQUEIO_MS = 2 * 60 * 1000;

  /**
   * Aviso efêmero na tela (ex.: "não detectei sua voz"). NUNCA entra em `mensagens` — logo nunca
   * vai no histórico enviado à IA — e some sozinho no próximo envio (texto, voz ou confirmação).
   */
  readonly avisoEfemero = signal<string | null>(null);

  /** Áudio do TTS em reprodução — guardado para que o barge-in possa interrompê-lo. */
  private audioAtual: HTMLAudioElement | null = null;

  /** Fila de blocos WAV (base64) do TTS incremental (voice/stream) — tocados em sequência. */
  private filaAudio: string[] = [];

  constructor() {
    super();
    // Espelha o Subject no signal pendente (estado puro — vale no browser e no SSR).
    this.prefill$.subscribe(t => this.prefillPendente.set(t));
  }
  /** Ações da última resposta do backend — consumidas pelo overlay para despacho. */
  readonly ultimasAcoes = signal<Acao[]>([]);

  /** Progresso ao vivo do turno (streaming SSE): "Consultando seus dados…" etc. Null = sem progresso. */
  readonly progresso = signal<string | null>(null);

  /** Alertas ativos da empresa logada (Área E2). */
  readonly alertas = signal<AlertaAssistente[]>([]);
  readonly naoLidos = computed(() => this.alertas().filter(a => !a.lidoEm).length);
  /** Alerta a exibir na tela atual (recomendação contextual), ou null. */
  readonly alertaContextual = signal<AlertaAssistente | null>(null);
  private readonly THROTTLE_CONTEXTUAL_MS = 120 * 60 * 1000;

  /** Primeiro nome do usuário logado (para a saudação). */
  readonly primeiroNome = computed(() => {
    const u = this.auth.getUserInfos() as any;
    const nome = (u?.nomeUsuario ?? '').toString().trim();
    return nome ? nome.split(' ')[0] : '';
  });

  /** Cor da marca da empresa (corHex do token), para o glow do orb. */
  readonly corMarca = computed<string | null>(() => {
    const u = this.auth.getUserInfos() as any;
    let emp = u?.empresaLogada ?? u?.empresaLogadaStruct;
    if (typeof emp === 'string') { try { emp = JSON.parse(emp); } catch { emp = null; } }
    const cor = emp?.preferenciaEmpresaStruct?.corHex;
    return typeof cor === 'string' && cor.startsWith('#') ? cor : null;
  });

  abrir(): void {
    this.aberto.set(true);
    if (this.mensagens().length === 0) {
      const nome = this.primeiroNome();
      const saud = nome ? `Oi, ${nome}! ` : 'Oi! ';
      this.mensagens.set([{ role: 'assistant', texto: `${saud}Como posso te ajudar hoje?`, hora: this.horaAgora() }]);
    }
  }

  private iniciarBloqueio(): void {
    this.ocupado.set(true);
    if (this.bloqueioTimer) clearTimeout(this.bloqueioTimer);
    this.bloqueioTimer = setTimeout(() => this.liberarBloqueio(), AiAgentService.FALLBACK_BLOQUEIO_MS);
  }

  private liberarBloqueio(): void {
    if (this.bloqueioTimer) {
      clearTimeout(this.bloqueioTimer);
      this.bloqueioTimer = null;
    }
    this.ocupado.set(false);
  }

  /** Hora local HH:MM para o carimbo da bolha. */
  private horaAgora(): string {
    return new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }

  fechar(): void {
    this.aberto.set(false);
  }

  novaConversa(): void {
    this.sessionId.set(null);
    this.mensagens.set([]);
    this.avisoEfemero.set(null);
    this.estado.set('ocioso');
  }

  /**
   * Envia `texto` ao backend. `display` (opcional) é o que aparece na bolha do usuário —
   * usado na desambiguação: envia o ID da opção mas mostra o rótulo legível.
   */
  async converseTexto(texto: string, display?: string): Promise<void> {
    const t = (texto ?? '').trim();
    if (!t) return;
    // Anti-pergunta-dupla: enquanto a resposta anterior não chega (ou o fallback não abre a
    // trava), novas perguntas são descartadas — a UI também desabilita o botão, isto é o backstop.
    if (this.ocupado()) return;
    this.avisoEfemero.set(null);
    this.iniciarBloqueio();
    // O front é o intermediário de ESTADO: envia o histórico anterior (memória multi-turn) a cada
    // chamada → backend stateless, imune à duplicata de pod / falta de afinidade de sessão.
    // Mapeia ANTES de anexar a mensagem atual (que vai separada em `mensagem`).
    const historico = this.mensagens().map(m => ({ role: m.role, texto: m.texto }));
    this.mensagens.update(m => [...m, { role: 'user', texto: (display ?? t), hora: this.horaAgora() }]);
    this.estado.set('pensando');
    try {
      // Contexto de tela: rota atual + form vivo registrado — deixa o backend saber onde o
      // usuário está (não navegar para onde já está; não navegar com form sujo; agir na tela).
      const contexto = this.screenContext.snapshot();
      // Preenchimento contínuo: a tela tem estado (campos/itens) que a IA vai LER neste turno —
      // feedback imediato via canal efêmero (nunca vira bolha/histórico). Os eventos SSE de
      // progresso sobrescrevem ("Preenchendo na sua tela…"); o finally limpa.
      this.definirProgressoLeituraDeTela(contexto);
      const payload = {
        mensagem: t,
        sessionId: this.sessionId(),
        historico,
        contexto,
      };
      let res: ConverseResponse;
      try {
        // Caminho preferido: SSE com progresso ao vivo ("Consultando seus dados…").
        res = await this.converseViaStream(payload);
      } catch {
        // Fallback: endpoint clássico (backend antigo, proxy sem SSE, erro de stream).
        res = await firstValueFrom(this.http.post<ConverseResponse>(
          this.base + 'converse', payload, this.addHeaderToken()));
      }
      this.sessionId.set(res.sessionId);
      this.ultimasAcoes.set(res.acoes ?? []);
      this.mensagens.update(m => [...m, { ...this.mapConverseToMessage(res), hora: this.horaAgora() }]);
    } catch {
      this.ultimasAcoes.set([]);
      this.mensagens.update(m => [...m, { role: 'assistant', texto: 'Tive um problema agora. Pode tentar de novo?', hora: this.horaAgora() }]);
    } finally {
      this.estado.set('ocioso');
      this.progresso.set(null);
      this.liberarBloqueio();
    }
  }

  /**
   * "Lendo a tela…" (preenchimento contínuo): quando o snapshot leva estado real da tela
   * (valores de campos ou itens), o usuário vê que a IA está lendo o que ele digitou.
   * Vive no signal `progresso` (efêmero, no lugar do "pensando") — nunca vira bolha,
   * logo nunca entra no histórico reenviado à IA.
   */
  private definirProgressoLeituraDeTela(contexto: { campos?: unknown[]; itens?: unknown[] }): void {
    if ((contexto.campos?.length ?? 0) > 0 || (contexto.itens?.length ?? 0) > 0) {
      this.progresso.set('Lendo a tela…');
    }
  }

  /**
   * Telemetria de ação (fire-and-forget): registra no servidor o resultado REAL de cada ação
   * emitida pelo agente — executada, bloqueada pela allowlist, bloqueada por form sujo,
   * subperfil sem rota. NUNCA bloqueia nem lança: falha de telemetria não pode afetar navegação.
   */
  reportarAcao(tipoAcao: string, rota: string | null, resultado: string, subperfil: number | null = null): void {
    try {
      this.http.post(this.base + 'acao-telemetria', {
        tipoAcao, rota, resultado, subperfil, sessionId: this.sessionId(),
      }, this.addHeaderToken()).subscribe({ error: () => { /* melhor-esforço */ } });
    } catch { /* nunca propaga */ }
  }

  /**
   * POST /AIAgent/converse/stream (SSE): eventos `status` alimentam o signal `progresso` em tempo
   * real e o evento `resposta` traz o MESMO payload do /converse. Lança em qualquer anomalia —
   * o chamador cai no endpoint clássico.
   */
  private async converseViaStream(payload: object): Promise<ConverseResponse> {
    const auth = (this.addHeaderToken().headers as any)?.get?.('Authorization') ?? '';
    const resp = await fetch(this.base + 'converse/stream', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': auth },
      body: JSON.stringify(payload),
    });
    if (!resp.ok || !resp.body) throw new Error('stream indisponível');

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let resposta: ConverseResponse | null = null;

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buffer.indexOf('\n\n')) >= 0) {
        const bloco = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const evento = /^event: (.+)$/m.exec(bloco)?.[1];
        const data = /^data: (.+)$/m.exec(bloco)?.[1];
        if (!evento || !data) continue;
        if (evento === 'status') {
          try { this.progresso.set(JSON.parse(data).texto ?? null); } catch { /* cosmético */ }
        } else if (evento === 'resposta') {
          resposta = JSON.parse(data) as ConverseResponse;
        } else if (evento === 'erro') {
          throw new Error('erro no assistente');
        }
      }
    }
    if (!resposta) throw new Error('stream sem resposta final');
    return resposta;
  }

  async confirmar(token: string): Promise<void> {
    if (this.ocupado()) return;
    this.avisoEfemero.set(null);
    this.iniciarBloqueio();
    this.estado.set('pensando');
    try {
      const res = await firstValueFrom(this.http.post<ConfirmResponse>(
        this.base + 'confirm',
        { token, sessionId: this.sessionId() },
        this.addHeaderToken()));
      this.mensagens.update(m => [...m, { role: 'assistant', texto: res.mensagem, hora: this.horaAgora() }]);
    } catch {
      this.mensagens.update(m => [...m, { role: 'assistant', texto: 'Não consegui confirmar agora.', hora: this.horaAgora() }]);
    } finally {
      this.estado.set('ocioso');
      this.liberarBloqueio();
    }
  }

  async enviarVoz(audio: Blob): Promise<void> {
    // Anti-pergunta-dupla: mesmo backstop do converseTexto (o modo conversa/VAD também passa aqui).
    if (this.ocupado()) return;
    this.avisoEfemero.set(null);
    // Áudio vazio (silêncio/ruído/estalo) morre no front: sem round-trip de STT para responder
    // nada. O usuário não fica no escuro — recebe um aviso efêmero, fora da conversa persistida.
    if (await audioVazio(audio)) {
      this.avisoEfemero.set('Ops, não detectei sua voz no áudio. Pode tentar de novo?');
      this.estado.set('ocioso');
      return;
    }
    this.iniciarBloqueio();
    this.estado.set('pensando');

    // O front é o intermediário de ESTADO também na voz: anexa o histórico anterior
    // (conversa contínua/multi-turn) → backend stateless. Capturado ANTES da transcrição,
    // que só é adicionada à lista depois da resposta.
    const historico = this.mensagens().map(m => ({ role: m.role, texto: m.texto }));

    // O áudio já chega como WAV 16 kHz do AudioCaptureService — sem conversão intermediária.
    const form = new FormData();
    form.append('audio', audio, 'fala.wav');
    form.append('historico', JSON.stringify(historico));
    // Contexto de tela na voz (paridade com o /converse): sem ele o backend era cego à tela —
    // navegava para onde o usuário já estava e não podia preencher o form vivo por voz.
    const contextoVoz = this.screenContext.snapshot();
    this.definirProgressoLeituraDeTela(contextoVoz);
    form.append('contexto', JSON.stringify(contextoVoz));
    const sid = this.sessionId();
    if (sid) form.append('sessionId', sid);

    try {
      // Caminho preferido: SSE — transcrição na tela logo após o STT, resposta no fim do
      // pipeline (sem esperar o TTS) e a fala chegando em blocos na sequência.
      await this.enviarVozViaStream(form);
    } catch {
      // Só chega aqui se NENHUM evento foi processado (backend antigo, proxy sem SSE, erro de
      // conexão): o turno não anda no caminho de streaming — seguro reenviar pelo clássico.
      await this.enviarVozClassico(form);
    }
  }

  /**
   * POST /AIAgent/voice/stream (SSE): `transcricao` vira a bolha do usuário na hora, `status`
   * alimenta o progresso ao vivo (paridade com o texto), `resposta` fecha o turno no MESMO
   * instante em que o canal de texto fecharia e `audio` chega DEPOIS, em blocos, direto para a
   * fila de reprodução.
   *
   * Lança apenas quando nada foi processado — aí o fallback clássico é seguro. A partir do
   * primeiro evento o turno pertence a este caminho: o pipeline pode ter rodado no servidor,
   * e reenviar duplicaria a pergunta.
   */
  private async enviarVozViaStream(form: FormData): Promise<void> {
    const auth = (this.addHeaderToken().headers as any)?.get?.('Authorization') ?? '';
    // Sem Content-Type manual: o navegador põe o multipart/form-data com boundary.
    const resp = await fetch(this.base + 'voice/stream', {
      method: 'POST',
      headers: { 'Authorization': auth },
      body: form,
    });
    if (!resp.ok || !resp.body) throw new Error('stream indisponível');

    let recebeuEvento = false;
    let recebeuResposta = false;
    try {
      await lerEventosSse(resp.body, (evento, data) => {
        recebeuEvento = true;
        if (evento === 'transcricao') {
          const t = String(JSON.parse(data).transcricao ?? '');
          if (t) this.mensagens.update(m => [...m, { role: 'user', texto: t, hora: this.horaAgora() }]);
        } else if (evento === 'status') {
          try { this.progresso.set(JSON.parse(data).texto ?? null); } catch { /* cosmético */ }
        } else if (evento === 'resposta') {
          const res = JSON.parse(data) as VoiceResponse;
          recebeuResposta = true;
          this.sessionId.set(res.sessionId);
          this.ultimasAcoes.set(res.acoes ?? []);
          // Turno que morre antes do pipeline (STT vazio etc.) chega sem evento `transcricao`
          // prévio e com transcricao vazia no payload — não há bolha do usuário a exibir.
          this.mensagens.update(m => [...m, { ...this.mapVoiceToMessage(res), hora: this.horaAgora() }]);
          this.progresso.set(null);
          this.estado.set('ocioso');
          // Paridade com o clássico: resposta na tela abre a trava — falar não bloqueia perguntar.
          this.liberarBloqueio();
        } else if (evento === 'audio') {
          const b64 = JSON.parse(data).audioWavBase64;
          if (b64) this.enfileirarWav(b64);
        } else if (evento === 'erro') {
          throw new Error('erro no assistente de voz');
        }
      });
    } catch {
      if (!recebeuEvento) throw new Error('stream indisponível'); // nada aconteceu → fallback
      // Stream caiu no meio do turno: NUNCA reenviar (risco de pergunta dupla). Com a resposta
      // já na tela, só o áudio se perdeu; sem ela, fecha o turno com a mensagem amigável.
    }
    if (!recebeuEvento) throw new Error('stream sem eventos'); // 200 vazio → fallback
    if (!recebeuResposta) {
      this.ultimasAcoes.set([]);
      this.mensagens.update(m => [...m, { role: 'assistant', texto: 'Não consegui entender o áudio. Pode repetir?', hora: this.horaAgora() }]);
    }
    this.progresso.set(null);
    if (this.estado() === 'pensando') this.estado.set('ocioso');
    this.liberarBloqueio();
  }

  /** Endpoint clássico /voice (JSON único com o áudio embutido) — fallback do streaming. */
  private async enviarVozClassico(form: FormData): Promise<void> {
    // Sem headers manuais: o authInterceptor injeta o Authorization e, por ser FormData,
    // NÃO seta Content-Type — o navegador põe o multipart/form-data com boundary.
    try {
      const res = await firstValueFrom(this.http.post<VoiceResponse>(
        this.base + 'voice', form));
      this.sessionId.set(res.sessionId);
      this.ultimasAcoes.set(res.acoes ?? []);
      if (res.transcricao) this.mensagens.update(m => [...m, { role: 'user', texto: res.transcricao, hora: this.horaAgora() }]);
      this.mensagens.update(m => [...m, { ...this.mapVoiceToMessage(res), hora: this.horaAgora() }]);
      if (res.audioWavBase64) {
        // A resposta já chegou: a trava abre ANTES do TTS tocar — falar não bloqueia perguntar.
        this.liberarBloqueio();
        this.tocarWav(res.audioWavBase64);
        return;
      }
    } catch {
      this.ultimasAcoes.set([]);
      this.mensagens.update(m => [...m, { role: 'assistant', texto: 'Não consegui entender o áudio. Pode repetir?', hora: this.horaAgora() }]);
    } finally {
      this.liberarBloqueio();
    }
    this.estado.set('ocioso');
  }

  /** Método puro: mapeia ConverseResponse → AiMessage (testável sem efeitos colaterais). */
  mapConverseToMessage(res: ConverseResponse): AiMessage {
    return {
      role: 'assistant',
      texto: res.resposta,
      confirmToken: res.confirmToken ?? undefined,
      opcoes: res.opcoes ?? undefined,
      resultados: res.resultados ?? undefined,
      acoes: res.acoes ?? undefined,
      fontes: res.fontes ?? undefined,
    };
  }

  /** Método puro: mapeia VoiceResponse → AiMessage (testável sem efeitos colaterais). */
  mapVoiceToMessage(res: VoiceResponse): AiMessage {
    return {
      role: 'assistant',
      texto: res.resposta,
      confirmToken: res.confirmToken ?? undefined,
      opcoes: res.opcoes ?? undefined,
      resultados: res.resultados ?? undefined,
      acoes: res.acoes ?? undefined,
      fontes: res.fontes ?? undefined,
    };
  }

  /** Busca os alertas ativos. Falha degrada silenciosamente (spec §5): sem alertas, chat normal. */
  async carregarAlertas(): Promise<void> {
    try {
      const res = await firstValueFrom(this.http.get<AlertasResponse>(
        this.base + 'alertas', this.addHeaderToken()));
      this.alertas.set(res.alertas ?? []);
    } catch {
      this.alertas.set([]);
    }
  }

  async marcarLido(id: number): Promise<void> {
    this.alertas.update(as => as.map(a => a.id === id ? { ...a, lidoEm: new Date().toISOString() } : a));
    try {
      await firstValueFrom(this.http.post<AlertaAcaoResponse>(
        this.base + `alertas/${id}/lido`, {}, this.addHeaderToken()));
    } catch { /* estado local já refletiu; próxima carga resincroniza */ }
  }

  async dispensarAlerta(id: number): Promise<void> {
    this.alertas.update(as => as.filter(a => a.id !== id));
    if (this.alertaContextual()?.id === id) this.alertaContextual.set(null);
    try {
      await firstValueFrom(this.http.post<AlertaAcaoResponse>(
        this.base + `alertas/${id}/dispensar`, {}, this.addHeaderToken()));
    } catch { /* estado local já refletiu; próxima carga resincroniza */ }
  }

  /** Envia a pergunta sugerida como se o usuário a tivesse digitado, e marca lido (spec §3.4.3). */
  async enviarPerguntaDoAlerta(alerta: AlertaAssistente): Promise<void> {
    await this.marcarLido(alerta.id);
    await this.converseTexto(alerta.perguntaSugerida);
  }

  /** Recomendação contextual por tela (spec §3.4.4): throttle por rota via localStorage,
   * nunca reexibe alerta já lido/dispensado (ambos já saíram do signal `alertas`). */
  verificarContextual(urlAtual: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const rotaBase = urlAtual.split('?')[0];
    const candidato = this.alertas().find(a => !a.lidoEm && a.rotaAlvo === rotaBase);
    if (!candidato) { this.alertaContextual.set(null); return; }

    const chave = `ai_alerta_contextual_${candidato.rotaAlvo}`;
    const ultimo = Number(localStorage.getItem(chave) ?? '0');
    if (Date.now() - ultimo < this.THROTTLE_CONTEXTUAL_MS) { this.alertaContextual.set(null); return; }

    localStorage.setItem(chave, String(Date.now()));
    this.alertaContextual.set(candidato);
  }

  /**
   * Interrompe a fala da IA imediatamente. Usado pelo barge-in: quando o usuário fala por cima,
   * o TTS precisa calar na hora, não ao fim do arquivo.
   */
  pararFala(): void {
    this.filaAudio = []; // blocos ainda não tocados morrem junto — barge-in cala a IA inteira
    const audio = this.audioAtual;
    this.audioAtual = null;
    if (audio) {
      audio.onended = null;
      audio.onerror = null;
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch {
        /* elemento já descartado */
      }
    }
    if (this.estado() === 'falando') this.estado.set('ocioso');
  }

  private tocarWav(base64: string): void {
    if (!isPlatformBrowser(this.platformId) || this.mutado()) { this.estado.set('ocioso'); return; }
    this.pararFala();
    this.enfileirarWav(base64);
  }

  /** Enfileira um bloco de fala do TTS e inicia a reprodução se nada estiver tocando. */
  private enfileirarWav(base64: string): void {
    if (!isPlatformBrowser(this.platformId) || this.mutado()) return;
    this.filaAudio.push(base64);
    if (!this.audioAtual) this.tocarProximoDaFila();
  }

  private tocarProximoDaFila(): void {
    const b64 = this.filaAudio.shift();
    if (!b64) {
      if (this.estado() === 'falando') this.estado.set('ocioso');
      return;
    }
    try {
      this.estado.set('falando');
      const audio = new Audio('data:audio/wav;base64,' + b64);
      this.audioAtual = audio;
      const fim = () => {
        // Só o dono corrente encadeia o próximo bloco: um pararFala no meio troca audioAtual,
        // e o callback do áudio antigo não pode ressuscitar a fila.
        if (this.audioAtual === audio) {
          this.audioAtual = null;
          this.tocarProximoDaFila();
        }
      };
      audio.onended = fim;
      audio.onerror = fim;
      audio.play().catch(fim);
    } catch {
      this.audioAtual = null;
      if (this.estado() === 'falando') this.estado.set('ocioso');
    }
  }
}
